import type { ErrorCode, ErrorDetail } from '@cusp/contracts';

/**
 * Thrown for any non-2xx response.
 *
 * The API emits a coded envelope — `{ error: { code, message, details?,
 * requestId } }` — so branch on `code`, never on `message`: messages are for
 * humans and get reworded. `requestId` echoes the `x-request-id` response
 * header and is what ties a user's report to the server logs.
 *
 * `details` CARRIES THE REASON WHEN `code` CANNOT (added 2026-09-01, for the
 * seat-assignment screen). A code is one word for a whole class of refusal,
 * and on the routes that refuse for several distinct reasons it is not enough
 * to act on: `POST /v1/admin/licenses/:id/seats/assign` answers CONFLICT for
 * four different things — `license_not_active`, `license_not_started`,
 * `license_ended` and `seat_capacity_exceeded` — and each of them is a
 * different sentence and a different next step for the administrator reading
 * it. Without this field the only thing left to tell them apart is the prose
 * of `message`, which the comment above forbids matching on and which is
 * reworded freely.
 *
 * It is a FAITHFUL COPY of what the server sent, never a guess: `ErrorDetail`
 * is already on the wire for every error (`ErrorBody` in `@cusp/contracts`),
 * and an absent or malformed `details` array stays `undefined` here rather
 * than becoming an empty one — "the server said nothing" and "the server said
 * there were no details" are different facts, and a screen that treats the
 * first as the second reports a refusal it cannot actually name.
 *
 * `path` is a FIELD PATH inside the request, not a URL: `{ path: 'userId',
 * message: 'not_a_member' }`. Read `message` for the machine key.
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly path: string;
  public readonly code: ErrorCode | undefined;
  public readonly requestId: string | undefined;
  /** The server's own field-level reasons, verbatim. `undefined` if it sent none. */
  public readonly details: readonly ErrorDetail[] | undefined;

  constructor(
    message: string,
    status: number,
    path: string,
    code?: ErrorCode,
    requestId?: string,
    details?: readonly ErrorDetail[],
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.path = path;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

/**
 * The machine keys the server attached to this refusal, in the order it sent
 * them. Empty for anything that is not an `ApiError` and for one that carries
 * no details, so a caller can `.includes(...)` without narrowing first.
 *
 * The KEY IS `message`, which reads backwards until you have seen the wire:
 * `ErrorDetail` is `{ path, message }`, `path` names the field and `message`
 * carries the reason — `{ path: 'endsAt', message: 'license_ended' }`.
 */
export function errorReasons(err: unknown): readonly string[] {
  return err instanceof ApiError && err.details ? err.details.map((d) => d.message) : [];
}

/**
 * 4xx means the request itself was wrong, so sending it again will be wrong
 * again.
 */
export function isClientError(err: unknown): boolean {
  return err instanceof ApiError && err.status >= 400 && err.status < 500;
}

/** The caller has no usable identity — prompt for one rather than retrying. */
export function isAuthError(err: unknown): boolean {
  return err instanceof ApiError && (err.code === 'UNAUTHENTICATED' || err.status === 401);
}

/** The caller is known but lacks an entitlement — the cue to offer a purchase. */
export function isNotEntitled(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'NOT_ENTITLED';
}
